import { useOutletContext, Link } from "react-router-dom";
import { StatusPill } from "../../components/StatusPill";
import { Clock3, CalendarClock, CheckCircle2, MessageSquarePlus, TrendingUp, Sparkles } from "lucide-react";
import { Button } from "../../components/ui/button";

export default function PortalOverview() {
  const { summary, analytics, requests, openRequestDialog } = useOutletContext();
  if (!summary) return <div className="p-8 text-neutral-500">Loading…</div>;
  const { client, stats } = summary;
  const openRequests = requests.filter(r=>r.status==="Open").length;
  const totalReach = analytics?.totals?.reach || 0;
  const upcoming = [...(stats.upcoming || [])].sort((a,b)=>(a.publishing_date+a.publishing_time).localeCompare(b.publishing_date+b.publishing_time)).slice(0,6);
  const recentRequests = requests.slice(0,4);

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-[1600px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Command Center</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">{client.client_name}</h1>
          <p className="text-neutral-600 text-sm mt-1">{client.industry} · {client.package} Package</p>
        </div>
        <div className="text-[11px] font-mono text-neutral-500 uppercase tracking-wider">
          {new Date().toLocaleDateString(undefined, {weekday:"long", month:"short", day:"numeric", year:"numeric"})}
        </div>
      </div>

      {/* Stat bento */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={<Clock3 className="w-4 h-4"/>} label="Awaiting Approval" value={stats.pending_approvals.length} tint="amber" testid="stat-pending"/>
        <StatCard icon={<CalendarClock className="w-4 h-4"/>} label="Upcoming" value={stats.upcoming.length} tint="blue" testid="stat-upcoming"/>
        <StatCard icon={<CheckCircle2 className="w-4 h-4"/>} label="Live Posts" value={stats.published.length} tint="violet" testid="stat-published"/>
        <StatCard icon={<MessageSquarePlus className="w-4 h-4"/>} label="Open Requests" value={openRequests} tint="emerald" testid="stat-open-requests"/>
        <StatCard icon={<TrendingUp className="w-4 h-4"/>} label="Total Reach" value={totalReach.toLocaleString()} tint="indigo" testid="stat-reach"/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upcoming */}
        <section className="panel p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg tracking-tight">Upcoming Posts</h3>
            <Link to="/portal/calendar" className="text-xs font-mono text-orange-400 hover:text-orange-700 uppercase tracking-wider">Open Calendar →</Link>
          </div>
          <div className="space-y-2">
            {upcoming.length === 0 && <div className="text-sm text-neutral-500">Nothing on the horizon.</div>}
            {upcoming.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-white/[0.03] border border-transparent hover:border-[#E4E7EC] transition-colors" data-testid={`overview-upcoming-${c.id}`}>
                <div className="w-9 h-9 rounded-md bg-gradient-to-br from-orange-500/30 to-red-500/20 border border-orange-500/20 grid place-items-center text-[10px] font-mono uppercase">
                  {(c.platform||"?").slice(0,2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.content_topic || c.content_type}</div>
                  <div className="text-[11px] font-mono text-neutral-500 uppercase">{c.platform} · {c.content_type}</div>
                </div>
                <div className="text-[11px] font-mono text-neutral-600">{c.publishing_date} {c.publishing_time}</div>
                <StatusPill status={c.status}/>
              </div>
            ))}
          </div>
        </section>

        {/* Right column: Approvals CTA + Requests preview */}
        <div className="space-y-4">
          {stats.pending_approvals.length > 0 ? (
            <section className="panel p-6 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-amber-500/15 blur-3xl"/>
              <div className="relative">
                <div className="text-[11px] font-mono uppercase tracking-wider text-amber-700">Awaiting Your Approval</div>
                <div className="text-4xl font-extrabold tracking-tight mt-1">{stats.pending_approvals.length}</div>
                <p className="text-neutral-600 text-sm mt-1">Review and approve or send changes back to your team.</p>
                <Link to="/portal/approvals" data-testid="cta-approvals">
                  <Button className="mt-4 w-full bg-amber-500 hover:bg-amber-600 text-neutral-900 font-semibold">Review approvals</Button>
                </Link>
              </div>
            </section>
          ) : (
            <section className="panel p-6">
              <div className="text-[11px] font-mono uppercase tracking-wider text-emerald-700">All caught up</div>
              <div className="text-lg font-bold tracking-tight mt-1">Nothing awaits your approval</div>
              <p className="text-neutral-600 text-sm mt-1">We'll ping you here as soon as your team drops new work.</p>
            </section>
          )}

          <section className="panel p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg tracking-tight">Recent Requests</h3>
              <Button size="sm" variant="ghost" onClick={openRequestDialog} data-testid="overview-new-request" className="text-orange-700"><Sparkles className="w-3.5 h-3.5 mr-1"/>New</Button>
            </div>
            <div className="space-y-2">
              {recentRequests.length === 0 && <div className="text-sm text-neutral-500">You have not raised any request yet.</div>}
              {recentRequests.map(r => (
                <div key={r.id} className="flex items-center gap-2 py-2 border-b border-[#E4E7EC] last:border-0" data-testid={`overview-request-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.title}</div>
                    <div className="text-[10px] font-mono uppercase text-neutral-500">{r.priority} · {(r.created_at||"").slice(0,10)}</div>
                  </div>
                  <span className={`pill ${r.status==="Open"?"pill-pending":r.status==="Delivered"?"pill-published":"pill-scheduled"}`}>{r.status}</span>
                </div>
              ))}
              <Link to="/portal/requests" className="block text-xs font-mono text-orange-400 uppercase tracking-wider pt-2">See all →</Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, tint, testid }) {
  const tints = {
    indigo: "from-orange-500/20 to-orange-500/5 border-orange-500/20 text-orange-700",
    amber: "from-amber-500/20 to-amber-500/5 border-amber-500/20 text-amber-700",
    blue: "from-blue-500/20 to-blue-500/5 border-blue-500/20 text-blue-700",
    violet: "from-red-500/20 to-red-500/5 border-red-500/20 text-red-700",
    emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/20 text-emerald-700",
  }[tint];
  return (
    <div className="panel p-5 relative overflow-hidden" data-testid={testid}>
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${tints} opacity-40 blur-2xl`}/>
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border ${tints} bg-gradient-to-br ${tints} font-mono text-[10px] uppercase tracking-wider`}>
        {icon}{label}
      </div>
      <div className="mt-3 text-3xl font-extrabold tracking-tight">{value}</div>
    </div>
  );
}
